import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Tabs,
  Table,
  Tag,
  Select,
  Row,
  Col,
  Statistic,
  Descriptions,
  Typography,
  Empty,
  message,
} from "antd";
import moment from "moment";
import PayrollService from "Services/EmployeeServices/PayrollService";
import BonusService from "Services/EmployeeServices/BonusService";
import W2Service from "Services/EmployeeServices/W2Service";
import { BonusTypeEnumLabel } from "Constants/Enums/BonusTypeEnum";
import { BonusStatusEnumLabel, BonusStatusColor } from "Constants/Enums/BonusStatusEnum";

const { Text } = Typography;

// Format money using the record's currency (W-2 currency may be null when no
// paycheck exists for the year).
const money = (v, currency) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  const amt = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${amt}` : amt;
};

const fmtDate = (d) => (d ? moment(d).format("DD MMM YYYY") : "-");
const periodLabel = (r) => `${fmtDate(r.periodStart)} - ${fmtDate(r.periodEnd)}`;

const bonusTypeLabel = (v) =>
  BonusTypeEnumLabel[v] || (typeof v === "string" ? v : String(v ?? "-"));
const bonusStatusTag = (v) => {
  const label = BonusStatusEnumLabel[v] || (typeof v === "string" ? v : String(v ?? "-"));
  return <Tag color={BonusStatusColor[v] || "default"}>{label}</Tag>;
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [0, 1, 2, 3, 4].map((i) => {
  const y = currentYear - i;
  return { value: y, label: String(y) };
});

// Shared Pay History / Bonus / W-2 display.
// employeeId absent (self view, uses /me endpoints) or a specific employee id
// (HR view, uses /employee/{id} endpoints). Read-only in both cases.
const PayBonusW2Panel = ({ employeeId }) => {
  const isSelf = employeeId === undefined || employeeId === null;

  const [activeTab, setActiveTab] = useState("pay");

  const [payLoading, setPayLoading] = useState(false);
  const [payHistory, setPayHistory] = useState(null);

  const [bonusLoading, setBonusLoading] = useState(false);
  const [bonuses, setBonuses] = useState([]);
  const [bonusLoaded, setBonusLoaded] = useState(false);

  const [w2Year, setW2Year] = useState(currentYear);
  const [w2Loading, setW2Loading] = useState(false);
  const [w2, setW2] = useState(null);

  const loadPay = async () => {
    try {
      setPayLoading(true);
      const data = isSelf
        ? await PayrollService.getMyPayHistory()
        : await PayrollService.getEmployeePayHistory(employeeId);
      setPayHistory(data || null);
    } catch (e) {
      message.error("Failed to load pay history");
      setPayHistory(null);
    } finally {
      setPayLoading(false);
    }
  };

  const loadBonuses = async () => {
    try {
      setBonusLoading(true);
      const data = isSelf
        ? await BonusService.getMine()
        : await BonusService.getByEmployee(employeeId);
      setBonuses(Array.isArray(data) ? data : []);
      setBonusLoaded(true);
    } catch (e) {
      message.error("Failed to load bonus history");
      setBonuses([]);
    } finally {
      setBonusLoading(false);
    }
  };

  const loadW2 = async (year) => {
    try {
      setW2Loading(true);
      const data = isSelf
        ? await W2Service.getMySummary(year)
        : await W2Service.getEmployeeSummary(employeeId, year);
      setW2(data || null);
    } catch (e) {
      message.error("Failed to load W-2 summary");
      setW2(null);
    } finally {
      setW2Loading(false);
    }
  };

  // Load pay history on mount, and reset/reload when the target employee changes.
  useEffect(() => {
    setBonusLoaded(false);
    setBonuses([]);
    setW2(null);
    setActiveTab("pay");
    loadPay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  // Bonus + W-2 load the first time their tab is opened.
  useEffect(() => {
    if (activeTab === "bonus" && !bonusLoaded) loadBonuses();
    if (activeTab === "w2" && w2 === null && !w2Loading) loadW2(w2Year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleYearChange = (year) => {
    setW2Year(year);
    loadW2(year);
  };

  const paychecks = Array.isArray(payHistory?.paychecks) ? payHistory.paychecks : [];

  const payColumns = [
    { title: "Pay Period", key: "period", render: (_, r) => periodLabel(r) },
    { title: "Regular", key: "regular", render: (_, r) => money(r.regularGrossPay, r.currency) },
    { title: "Bonus", key: "bonus", render: (_, r) => money(r.bonusPay, r.currency) },
    { title: "Gross", key: "gross", render: (_, r) => money(r.grossPay, r.currency) },
    { title: "Deductions", key: "deductions", render: (_, r) => money(r.totalDeductions, r.currency) },
    {
      title: "Net Pay",
      key: "net",
      render: (_, r) => <Text strong>{money(r.netPay, r.currency)}</Text>,
    },
  ];

  const renderPaycheckDetail = (r) => (
    <div>
      <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Federal Tax">{money(r.federalTax, r.currency)}</Descriptions.Item>
        <Descriptions.Item label="State Tax">{money(r.stateTax, r.currency)}</Descriptions.Item>
        <Descriptions.Item label="Other Deductions">
          {money(r.otherDeductions, r.currency)}
        </Descriptions.Item>
        <Descriptions.Item label="Unpaid Leave Deduction">
          {money(r.unpaidLeaveDeduction, r.currency)}
        </Descriptions.Item>
      </Descriptions>

      {Array.isArray(r.leaveDeductions) && r.leaveDeductions.length > 0 && (
        <>
          <Text strong>Unpaid leave in this period</Text>
          <Table
            size="small"
            rowKey={(d, i) => `${d.leaveRequestId}-${i}`}
            pagination={false}
            style={{ margin: "8px 0 16px" }}
            dataSource={r.leaveDeductions}
            columns={[
              { title: "Leave Request", dataIndex: "leaveRequestId", key: "lr" },
              { title: "Unpaid Hours", dataIndex: "unpaidHours", key: "uh" },
              { title: "Unpaid Days", dataIndex: "unpaidDays", key: "ud" },
              { title: "Amount", key: "amt", render: (_, d) => money(d.amount, r.currency) },
            ]}
          />
        </>
      )}

      {Array.isArray(r.bonuses) && r.bonuses.length > 0 && (
        <>
          <Text strong>Bonuses in this paycheck</Text>
          <Table
            size="small"
            rowKey={(b, i) => `${b.id}-${i}`}
            pagination={false}
            style={{ marginTop: 8 }}
            dataSource={r.bonuses}
            columns={[
              { title: "Type", key: "bt", render: (_, b) => bonusTypeLabel(b.bonusType) },
              {
                title: "Amount",
                key: "ba",
                render: (_, b) => money(b.amount, b.currency || r.currency),
              },
              { title: "Award Date", key: "bd", render: (_, b) => fmtDate(b.awardDate) },
              { title: "Status", key: "bs", render: (_, b) => bonusStatusTag(b.status) },
            ]}
          />
        </>
      )}

      {!(r.leaveDeductions && r.leaveDeductions.length) &&
        !(r.bonuses && r.bonuses.length) && (
          <Text type="secondary">No bonuses or unpaid-leave deductions in this paycheck.</Text>
        )}
    </div>
  );

  const bonusColumns = [
    { title: "Type", key: "type", render: (_, r) => bonusTypeLabel(r.bonusType) },
    { title: "Amount", key: "amount", render: (_, r) => money(r.amount, r.currency) },
    { title: "Status", key: "status", render: (_, r) => bonusStatusTag(r.status) },
    { title: "Award Date", key: "award", render: (_, r) => fmtDate(r.awardDate) },
    { title: "Paid On", key: "paid", render: (_, r) => fmtDate(r.paidOn) },
    { title: "In Payroll", key: "payroll", render: (_, r) => (r.payrollId ? `#${r.payrollId}` : "-") },
    { title: "Reason", dataIndex: "reason", key: "reason", render: (v) => v || "-" },
  ];

  const items = [
    {
      key: "pay",
      label: "Pay History",
      children: (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={8}>
              <Card size="small">
                <Statistic
                  title="Total Gross (to date)"
                  value={payHistory?.cumulativeGrossPay ?? 0}
                  precision={2}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small">
                <Statistic
                  title="Total Net (to date)"
                  value={payHistory?.cumulativeNetPay ?? 0}
                  precision={2}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small">
                <Statistic title="Paychecks" value={payHistory?.paycheckCount ?? 0} />
              </Card>
            </Col>
          </Row>
          <Table
            className="custom-table"
            rowKey={(r) => r.id}
            loading={payLoading}
            columns={payColumns}
            dataSource={paychecks}
            pagination={{ pageSize: 8 }}
            expandable={{ expandedRowRender: renderPaycheckDetail }}
            locale={{ emptyText: "No paychecks yet" }}
          />
        </>
      ),
    },
    {
      key: "bonus",
      label: "Bonuses",
      children: (
        <Table
          className="custom-table"
          rowKey={(r) => r.id}
          loading={bonusLoading}
          columns={bonusColumns}
          dataSource={bonuses}
          pagination={{ pageSize: 8 }}
          locale={{ emptyText: "No bonuses yet" }}
        />
      ),
    },
    {
      key: "w2",
      label: "W-2 Summary",
      children: (
        <>
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ marginRight: 8 }}>
              Tax Year
            </Text>
            <Select
              value={w2Year}
              style={{ width: 120 }}
              onChange={handleYearChange}
              options={YEAR_OPTIONS}
            />
          </div>
          {w2Loading ? (
            <Card loading />
          ) : w2 && w2.paycheckCount > 0 ? (
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Tax Year">{w2.taxYear}</Descriptions.Item>
              <Descriptions.Item label="Paychecks">{w2.paycheckCount}</Descriptions.Item>
              <Descriptions.Item label="Regular Gross">
                {money(w2.regularGrossPay, w2.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Bonus Pay">{money(w2.bonusPay, w2.currency)}</Descriptions.Item>
              <Descriptions.Item label="Total Gross">
                {money(w2.totalGrossPay, w2.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Federal Tax Withheld">
                {money(w2.federalTaxWithheld, w2.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="State Tax Withheld">
                {money(w2.stateTaxWithheld, w2.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Other Deductions">
                {money(w2.otherDeductions, w2.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Unpaid Leave Deductions">
                {money(w2.unpaidLeaveDeductions, w2.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Total Net Pay">
                {money(w2.totalNetPay, w2.currency)}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Empty description={`No paychecks for ${w2Year}`} />
          )}
        </>
      ),
    },
  ];

  return <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} />;
};

PayBonusW2Panel.propTypes = {
  employeeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default PayBonusW2Panel;
